import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { projects } from "@/data/projects";
import { services } from "@/data/services";
import { whyChoose, testimonials, clientLogos } from "@/data/team";
import { HighlightBrand } from "@/components/HighlightBrand";
import VideoModal from "@/components/VideoModal";
import { ArrowUpRight, ArrowDown, Download, Film, Camera, Clapperboard, Boxes, Sparkles, Feather, ShieldCheck, Timer, Cpu, Mountain, Music2, Building2, Landmark, Waves, Castle, Hexagon, Signal, Gem, Triangle, PlayCircle, Star, Aperture, Palette, Mic, Plane, Megaphone } from "lucide-react";
import { toast } from "sonner";
import HomeBlogTeaser from "@/components/HomeBlogTeaser";

const iconMap = { Film, Camera, Clapperboard, Boxes, Sparkles, Feather, ShieldCheck, Timer, Cpu, Mountain, Music2, Building2, Landmark, Waves, Castle, Hexagon, Signal, Gem, Triangle, Star, Aperture, Palette, Mic, Plane, Megaphone };

const HERO_IMG = "https://images.pexels.com/photos/10395639/pexels-photo-10395639.jpeg";
const INTRO_VIDEO_URL = "https://www.youtube.com/watch?v=dUzBg98TveE";

const Home = () => {
  const { t, lang } = useLang();
  const [introOpen, setIntroOpen] = useState(false);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden grain-overlay" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Cinematic still by ARTLENSS"
            className="w-full h-full object-cover scale-110"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-[#0A0A0C]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-transparent to-transparent" />
        </div>

        <div className="relative container-x pt-40 pb-2 md:pt-52 md:pb-4 min-h-[100svh] flex flex-col justify-center -translate-y-6 md:-translate-y-10 rtl:-translate-y-20 md:rtl:-translate-y-28">
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="overline"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-anniversary-badge"
            className="mt-6 max-w-3xl"
          >
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight text-white italic">
              {t("hero.badgeTitle")}
            </h2>
            <p className="mt-3 text-white/70 text-base md:text-lg max-w-2xl">{t("hero.badgeSub")}</p>
            <div className="mt-6 h-px w-24 bg-[#E6A95B]/60" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="display-h1 mt-8 rtl:-mt-2 max-w-5xl font-display"
          >
            <span className="block">{t("hero.title1")}</span>
            <span className="block italic text-[#E6A95B]">{t("hero.title2")}</span>
            <span className="block">{t("hero.title3")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 rtl:mt-2 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed"
          >
            <HighlightBrand text={t("hero.sub")} />
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 rtl:mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link to="/work" data-testid="hero-view-work" className="btn-primary group">
              {t("hero.viewWork")} <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 -translate-y-0.5 transition" />
            </Link>
            <Link to="/contact" data-testid="hero-start-project" className="btn-ghost">
              {t("hero.startProject")}
            </Link>
            <a
              href={lang === "ar" ? "/downloads/ArtLens_AR.pdf" : "/downloads/ArtLens_EN.pdf"}
              download={lang === "ar" ? "ArtLens_AR.pdf" : "ArtLens_EN.pdf"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-download-profile"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E6A95B]/60 text-[#E6A95B] font-medium tracking-wide hover:bg-[#E6A95B] hover:text-[#0A0A0C] hover:border-[#E6A95B] hover:shadow-[0_0_30px_-5px_rgba(230,169,91,0.6)] transition-all duration-300"
            >
              <Download className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
              {t("hero.downloadProfile")}
            </a>
            <button
              type="button"
              onClick={() => setIntroOpen(true)}
              data-testid="hero-watch-intro"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 text-white/90 font-medium tracking-wide hover:border-[#E6A95B] hover:text-[#E6A95B] hover:shadow-[0_0_30px_-5px_rgba(230,169,91,0.5)] transition-all duration-300"
            >
              <PlayCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
              {t("hero.watchIntro")}
            </button>
            <div className="sm:ms-10 md:ms-14 flex items-center gap-2 text-white/45 text-xs uppercase tracking-[0.3em]">
              {t("hero.scroll")} <ArrowDown className="w-4 h-4 animate-bounce" />
            </div>
          </motion.div>

          {/* removed absolute-bottom scroll */}
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="pt-4 pb-24 md:pt-6 md:pb-32 bg-[#0d0d10] border-y border-white/5" data-testid="services-overview">
        <div className="container-x">
          <div className="max-w-3xl mb-16">
            <div className="overline mb-4">{t("home.servicesEyebrow")}</div>
            <h2 className="display-h2">{t("home.servicesTitle")}</h2>
            <p className="mt-6 text-white/60 text-lg">{t("home.servicesSub")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Sparkles;
              return (
                <Link
                  key={s.id}
                  to={`/services#${s.id}`}
                  data-testid={`service-card-${s.id}`}
                  className="group bg-[#0A0A0C] p-8 hover:bg-[#121216] transition-all duration-500 flex flex-col"
                >
                  <div className="w-11 h-11 rounded-lg border border-[#E6A95B]/30 grid place-items-center text-[#E6A95B] group-hover:bg-[#E6A95B] group-hover:text-[#0A0A0C] transition">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-6 font-display text-xl md:text-2xl text-white">{s.title[lang]}</h3>
                  <p className="mt-3 text-white/55 text-sm leading-relaxed flex-1">{s.tagline[lang]}</p>
                  <div className="mt-6 flex items-center gap-1 text-[#E6A95B] text-sm">
                    <span>{t("services.requestQuote")}</span>
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 -translate-y-0.5 transition" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 md:py-32" data-testid="why-choose-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{t("home.whyEyebrow")}</div>
            <h2 className="display-h2">{t("home.whyTitle")}</h2>
            <p className="mt-6 text-white/60 text-lg">{t("home.whySub")}</p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-1 text-[#E6A95B]">
              <span className="font-semibold">{t("nav.about")}</span> <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {whyChoose.map((w, i) => {
              const Icon = iconMap[w.icon] || Sparkles;
              return (
                <div key={i} className="glass-card p-8 rounded-xl card-hover" data-testid={`why-item-${i}`}>
                  <Icon className="w-6 h-6 text-[#E6A95B]" />
                  <h4 className="mt-5 font-display text-xl">{w.title[lang]}</h4>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed">{w.desc[lang]}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#0d0d10] border-y border-white/5" data-testid="testimonials-section">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <div className="overline mb-4">{t("home.testEyebrow")}</div>
            <h2 className="display-h2">{t("home.testTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tt, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="glass-card p-8 md:p-10 rounded-xl">
                <div className="text-[#E6A95B] font-display text-5xl leading-none">&ldquo;</div>
                <blockquote className="mt-2 text-white/85 text-base md:text-lg leading-relaxed">{tt.quote[lang]}</blockquote>
                <figcaption className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-semibold">{tt.name[lang]}</div>
                  <div className="text-white/50 text-sm">{tt.org[lang]}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS MARQUEE */}
      <section className="py-20" data-testid="clients-strip">
        <div className="container-x mb-10">
          <div className="overline">{t("home.clientsEyebrow")}</div>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-white/80">{t("home.clientsTitle")}</h3>
        </div>
        <div className="marquee-mask overflow-hidden">
          <div className="flex gap-16 animate-marquee marquee-track whitespace-nowrap w-max">
            {[...clientLogos, ...clientLogos, ...clientLogos, ...clientLogos].map((c, i) => {
              const Icon = iconMap[c.icon] || Mountain;
              return (
                <div key={i} className="group flex items-center gap-3 text-white/45 hover:text-[#E6A95B] transition">
                  {c.image ? (
                    <span
                      aria-label={c.name}
                      className="h-12 w-12 inline-block bg-white/45 group-hover:bg-[#E6A95B] transition-colors duration-300"
                      style={{
                        WebkitMaskImage: `url(${c.image})`,
                        maskImage: `url(${c.image})`,
                        WebkitMaskRepeat: "no-repeat",
                        maskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                        maskPosition: "center",
                        WebkitMaskSize: "contain",
                        maskSize: "contain",
                      }}
                    />
                  ) : (
                    <Icon className="w-6 h-6" />
                  )}
                  <span className="font-display text-xl">{lang === "ar" && c.nameAr ? c.nameAr : c.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOME BLOG TEASER */}
      <HomeBlogTeaser />

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="closing-cta">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1619099619226-f96e319e3732" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] to-[#0A0A0C]/60" />
        </div>
        <div className="relative container-x text-center max-w-6xl mx-auto">
          <h2 className="display-h2 italic text-3xl sm:text-4xl md:text-5xl lg:text-6xl rtl:whitespace-nowrap rtl:text-6xl md:rtl:text-7xl lg:rtl:text-8xl">{t("contact.title")}</h2>
          <p className="mt-8 text-white/70 text-lg">{t("contact.lead")}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="closing-cta-start">
              {t("nav.cta")} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/work" className="btn-ghost">
              <PlayCircle className="w-4 h-4" /> {t("hero.viewWork")}
            </Link>
          </div>
        </div>
      </section>

      {/* Video modals — mounted once, controlled by state */}
      <VideoModal
        open={introOpen}
        onClose={() => setIntroOpen(false)}
        videoUrl={INTRO_VIDEO_URL}
        title={t("hero.watchIntro")}
      />
    </div>
  );
};

export default Home;
